"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getCurrentLiteracyEventFeedbackForStudent = getCurrentLiteracyEventFeedbackForStudent;
exports.getCurrentLiteracyEventTaskForStudent = getCurrentLiteracyEventTaskForStudent;
exports.getLiteracyEventsThatAreActive = getLiteracyEventsThatAreActive;
var _missionStateConstants = require("../constants/missionStateConstants");
var _students = require("./students");
const MIN_PERCENTAGE_OF_STUDENTS_WORKING_ON_LITERACY_EVENT = 0.2;
/**
 * getActiveLiteracyEvents - check which literacy events are active by checking percentage of connected students that are working on the literacy events.
 *
 * @param {?BothFullMissionStates} missionState - the current state of the mission
 * @returns {boolean} - true if the literacy event is deemed active, false otherwise
 */
function getLiteracyEventsThatAreActive(missionState) {
  if (!missionState) {
    return [];
  }
  const pausedLiteracyEventIds = getPausedLiteracyEventIds(missionState);
  const activeStudentIds = Object.keys((0, _students.getConnectedStudents)(missionState));
  const studentLitEventCounter = {
    current: {},
    assignedNext: {}
  };
  activeStudentIds.forEach(studentId => {
    missionState.literacyEvents.studentStates[studentId]?.literacyEventsStates?.forEach((literacyEventsState, index) => {
      if (pausedLiteracyEventIds.has(literacyEventsState.literacyEventId)) {
        return;
      }
      if (index === 0) {
        studentLitEventCounter.current[literacyEventsState.literacyEventId] = (studentLitEventCounter.current[literacyEventsState.literacyEventId] || 0) + 1;
      } else {
        studentLitEventCounter.assignedNext[literacyEventsState.literacyEventId] = (studentLitEventCounter.assignedNext[literacyEventsState.literacyEventId] || 0) + 1;
      }
    });
  });
  const literacyEvents = [];
  Object.keys(studentLitEventCounter.current).forEach(literacyEventId => {
    // Indicators the literacy event is active:
    // - More than 20% of the students are currently working on the literacy event
    // - If at least 1 student is working on the literacy event, and more than 20% of the students are
    // waiting to work on it.
    if (studentLitEventCounter.current[literacyEventId] >= 1 && (studentLitEventCounter.current[literacyEventId] + (studentLitEventCounter.assignedNext?.[literacyEventId] || 0)) / activeStudentIds.length > MIN_PERCENTAGE_OF_STUDENTS_WORKING_ON_LITERACY_EVENT) {
      literacyEvents.push({
        id: literacyEventId,
        actionId: missionState.literacyEvents.seenEvents[literacyEventId].actionId
      });
    }
  });
  return literacyEvents;
}

/**
 * getCurrentLiteracyEventTaskForStudent - get the current task that the given student should be working on
 *
 * @param {?BothFullMissionStates} missionState - the current state of the mission
 * @param {string} studentId - the id of the student to get the current task for
 *
 * @return {?GetCurrentLiteracyEventStudentTaskResult} - the current task the student should be working on, null or undefined if there is no task the student should be working on
 */
function getCurrentLiteracyEventTaskForStudent(missionState, studentId) {
  if (!missionState) {
    return null;
  }
  const literacyEventState = missionState.literacyEvents;
  const studentLiteracyEventStates = literacyEventState.studentStates?.[studentId]?.literacyEventsStates;
  const pausedLiteracyEventIds = getPausedLiteracyEventIds(missionState);
  if (!studentLiteracyEventStates) {
    return null;
  }

  // NOTE: the current task should be located at the `0`th index of each of these arrays (as they are all FIFO queues),
  // however, they are looped through in case a task type is not yet supported or elements did not get removed appropriately.
  for (let j = 0; j < studentLiteracyEventStates.length; j++) {
    const currentLiteracyEventState = studentLiteracyEventStates[j];
    if (pausedLiteracyEventIds.has(currentLiteracyEventState.literacyEventId)) {
      continue;
    }
    for (let k = 0; k < currentLiteracyEventState.readingContextStates.length; k++) {
      const currentStudentReadingContextState = currentLiteracyEventState.readingContextStates[k];
      const literacyEvent = literacyEventState.seenEvents[currentLiteracyEventState.literacyEventId];
      const readingContext = literacyEvent?.readingContext[currentStudentReadingContextState.readingContextId];
      for (let l = 0; l < currentStudentReadingContextState.taskStates.length; l++) {
        const currentStudentTaskState = currentStudentReadingContextState.taskStates[l];
        const currentTask = readingContext?.tasks[currentStudentTaskState.taskId];
        if (!literacyEvent || !readingContext || !currentTask || currentTask.type !== currentStudentTaskState.state.type) {
          // this should never happen, but in case it does, it is recoverable
          continue;
        }
        const nextTaskId = currentStudentReadingContextState.taskStates[l + 1]?.taskId;

        // flow gets confused, so each type is check separately
        if (currentTask.type === _missionStateConstants.LITERACY_EVENT.TASK.TYPE.MULTIPLE_CHOICE && currentStudentTaskState.state.type === _missionStateConstants.LITERACY_EVENT.TASK.TYPE.MULTIPLE_CHOICE) {
          return {
            id: literacyEvent.id,
            readingContext: {
              ...readingContext,
              instanceId: currentStudentReadingContextState.instanceId
            },
            taskData: {
              type: _missionStateConstants.LITERACY_EVENT.TASK.TYPE.MULTIPLE_CHOICE,
              task: currentTask,
              studentState: currentStudentTaskState.state,
              instanceId: currentStudentTaskState.instanceId,
              nextTaskId: nextTaskId
            }
          };
        }
        if (currentTask.type === _missionStateConstants.LITERACY_EVENT.TASK.TYPE.SPEAKING && currentStudentTaskState.state.type === _missionStateConstants.LITERACY_EVENT.TASK.TYPE.SPEAKING) {
          return {
            id: literacyEvent.id,
            readingContext: {
              ...readingContext,
              instanceId: currentStudentReadingContextState.instanceId
            },
            taskData: {
              type: _missionStateConstants.LITERACY_EVENT.TASK.TYPE.SPEAKING,
              task: currentTask,
              studentState: currentStudentTaskState.state,
              instanceId: currentStudentTaskState.instanceId,
              nextTaskId: nextTaskId
            }
          };
        }
        if (currentTask.type === _missionStateConstants.LITERACY_EVENT.TASK.TYPE.CLOSE_READING && currentStudentTaskState.state.type === _missionStateConstants.LITERACY_EVENT.TASK.TYPE.CLOSE_READING) {
          return {
            id: literacyEvent.id,
            readingContext: {
              ...readingContext,
              instanceId: currentStudentReadingContextState.instanceId
            },
            taskData: {
              type: _missionStateConstants.LITERACY_EVENT.TASK.TYPE.CLOSE_READING,
              task: currentTask,
              studentState: currentStudentTaskState.state,
              instanceId: currentStudentTaskState.instanceId,
              nextTaskId: nextTaskId
            }
          };
        }
        // the task and state type is not recognized, check next possible task
      }

      // there is no task in the reading context
      return {
        id: literacyEvent.id,
        readingContext: {
          ...readingContext,
          instanceId: currentStudentReadingContextState.instanceId
        },
        taskData: null
      };
    }
  }
  return null;
}

/**
 * getCurrentLiteracyEventFeedbackForStudent - get the next feedback request that the given student should complete
 *
 * @param {?BothFullMissionStates} missionState - the current mission's state
 * @param {string} studentId - the id of the student to get feedback from
 *
 * @return {?GetCurrentLiteracyEventFeedbackRequestResult}
 */
function getCurrentLiteracyEventFeedbackForStudent(missionState, studentId) {
  if (!missionState) {
    return null;
  }
  const currentFeedbackRequests = missionState.literacyEvents.studentFeedbackRequests[studentId];
  if (!currentFeedbackRequests?.length) {
    return null;
  }
  for (let i = 0; i < currentFeedbackRequests.length; i++) {
    const feedbackRequest = currentFeedbackRequests[i];
    const taskData = missionState?.literacyEvents.seenEvents[feedbackRequest.forLiteracyEventId]?.readingContext[feedbackRequest.forReadingContextId]?.tasks[feedbackRequest.forTaskId];
    if (!taskData || taskData.type !== _missionStateConstants.LITERACY_EVENT.TASK.TYPE.SPEAKING || taskData.target.type !== feedbackRequest.type) {
      // something is wrong with the state, skip this feedback request
      continue;
    }
    return {
      type: _missionStateConstants.LITERACY_EVENT.TASK.TYPE.SPEAKING,
      feedbackRequest,
      forTask: taskData
    };
  }
  return null;
}

/**
 * getPausedLiteracyEventIds - get the ids of literacy events which are currently paused
 *
 * @param {BothFullMissionStates} missionState - the current mission's state
 *
 * @return {Set<string>}
 */
function getPausedLiteracyEventIds(missionState) {
  return new Set(Object.keys(missionState.literacyEvents.seenEvents).filter(literacyEventId => missionState.literacyEvents.seenEvents[literacyEventId].status === _missionStateConstants.LITERACY_EVENT.LITERACY_EVENT_STATUS.PAUSED));
}